import { Alert, Button, Checkbox, Form, Input, Radio, Select, Space, Table, message } from 'antd';
import * as Component from 'components';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentApiService } from '../../../shared/service/api/contentApiService';
import { seasonApiService } from '../../../shared/service/api/seasonApiService';
import { seriesApiService } from '../../../shared/service/api/seriesApiService';
import { contentService } from '../../content/service/contentService';
import { burnInService } from '../services/burnInService';
import {
  BurnInSkippedItem,
  BurnInValidationItem,
  BurnInValidationResult,
} from '../types/burnInTypes';

type RequestMode = 'single' | 'batch';

interface ContentOption {
  id: number;
  title: string;
  slug: string;
}

interface SubtitleOption {
  id: number;
  lang: string;
}

interface SeriesOption {
  id: number;
  title: string;
}

interface SeasonOption {
  id: number;
  title: string;
}

const BurnInRequest: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [mode, setMode] = useState<RequestMode>('single');

  // Single mode state
  const [contentSearchResults, setContentSearchResults] = useState<ContentOption[]>([]);
  const [contentSearchLoading, setContentSearchLoading] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<number | null>(null);
  const [subtitles, setSubtitles] = useState<SubtitleOption[]>([]);
  const [subtitlesLoading, setSubtitlesLoading] = useState(false);
  const [selectedSubtitleIds, setSelectedSubtitleIds] = useState<number[]>([]);

  // Batch mode state
  const [seriesOptions, setSeriesOptions] = useState<SeriesOption[]>([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [seasonOptions, setSeasonOptions] = useState<SeasonOption[]>([]);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(null);
  const [batchLang, setBatchLang] = useState<string>('');
  const [validationResult, setValidationResult] = useState<BurnInValidationResult | null>(null);
  const [validating, setValidating] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // --- Single mode handlers ---

  const handleContentSearch = useCallback(async (searchText: string) => {
    if (!searchText || searchText.length < 2) {
      setContentSearchResults([]);
      return;
    }
    setContentSearchLoading(true);
    try {
      const params = new URLSearchParams({ search: searchText, page_size: '20' });
      const result = await contentApiService.fetch(params);
      setContentSearchResults(
        (result.results || []).map(c => ({
          id: c.id,
          title: c.title || c.title_en || c.slug,
          slug: c.slug,
        }))
      );
    } catch {
      message.error('Failed to search content');
    } finally {
      setContentSearchLoading(false);
    }
  }, []);

  const handleContentSelect = useCallback(async (contentId: number) => {
    setSelectedContentId(contentId);
    setSubtitles([]);
    setSelectedSubtitleIds([]);
    setSubtitlesLoading(true);
    try {
      const subs = await contentService.fetchSubtitleList(contentId);
      setSubtitles(subs.map(s => ({ id: s.id, lang: s.lang })));
    } catch {
      message.error('Failed to fetch subtitles for this content');
    } finally {
      setSubtitlesLoading(false);
    }
  }, []);

  const handleSubtitleToggle = useCallback(
    (subtitleId: number, checked: boolean) => {
      setSelectedSubtitleIds(prev =>
        checked ? [...prev, subtitleId] : prev.filter(id => id !== subtitleId)
      );
    },
    []
  );

  // --- Batch mode handlers ---

  const handleSeriesSearch = useCallback(async (searchText: string) => {
    if (!searchText || searchText.length < 2) {
      setSeriesOptions([]);
      return;
    }
    setSeriesLoading(true);
    try {
      const params = new URLSearchParams({ search: searchText, page_size: '20' });
      const result = await seriesApiService.fetch(params);
      setSeriesOptions(
        (result.results || []).map(s => ({
          id: s.id,
          title: s.title || s.title_en || s.slug,
        }))
      );
    } catch {
      message.error('Failed to search series');
    } finally {
      setSeriesLoading(false);
    }
  }, []);

  const handleSeriesSelect = useCallback(async (seriesId: number) => {
    setSelectedSeriesId(seriesId);
    setSelectedSeasonId(null);
    setSeasonOptions([]);
    setValidationResult(null);
    setSeasonLoading(true);
    try {
      const result = await seasonApiService.fetchSeasonsBySeriesWithI18n(seriesId);
      setSeasonOptions(
        (result.results || []).map(s => ({
          id: s.id,
          title: s.title || s.title_en || s.slug,
        }))
      );
    } catch {
      message.error('Failed to fetch seasons');
    } finally {
      setSeasonLoading(false);
    }
  }, []);

  const handleSeasonSelect = useCallback((seasonId: number) => {
    setSelectedSeasonId(seasonId);
    setValidationResult(null);
  }, []);

  const handleValidate = useCallback(async () => {
    if (!selectedSeasonId || !batchLang) {
      message.warning('Please select a season and enter a language');
      return;
    }
    setValidating(true);
    setValidationResult(null);
    try {
      const result = await burnInService.validate(selectedSeasonId, batchLang);
      setValidationResult(result);
    } catch {
      message.error('Validation failed');
    } finally {
      setValidating(false);
    }
  }, [selectedSeasonId, batchLang]);

  // --- Submit ---

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      if (mode === 'single') {
        if (!selectedContentId || selectedSubtitleIds.length === 0) {
          message.warning('Please select a content and at least one subtitle');
          return;
        }
        await Promise.all(
          selectedSubtitleIds.map(subtitleId =>
            burnInService.createSingleJob(selectedContentId, subtitleId)
          )
        );
        message.success(
          `Created ${selectedSubtitleIds.length} burn-in job(s) successfully`
        );
      } else {
        if (!selectedSeasonId || !batchLang) {
          message.warning('Please select a season and enter a language');
          return;
        }
        const jobs = await burnInService.createBatchJobs(selectedSeasonId, batchLang);
        message.success(`Created ${jobs.length} batch burn-in job(s) successfully`);
      }
      navigate('/burn-in');
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to create burn-in jobs';
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [mode, selectedContentId, selectedSubtitleIds, selectedSeasonId, batchLang, navigate]);

  // --- Table columns for validation results ---

  const availableColumns = [
    {
      title: 'Content',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Content ID',
      dataIndex: 'content_id',
      key: 'content_id',
    },
    {
      title: 'Video ID',
      dataIndex: 'video_id',
      key: 'video_id',
    },
  ];

  const skippedColumns = [
    {
      title: 'Content',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
    },
  ];

  const canSubmitSingle = selectedContentId && selectedSubtitleIds.length > 0;
  const canSubmitBatch = selectedSeasonId && batchLang;

  return (
    <Component.BasePageComponent>
      <h2>Subtitle Burn-in Request</h2>

      <Form form={form} layout="vertical">
        <Form.Item label="Mode">
          <Radio.Group
            value={mode}
            onChange={e => {
              setMode(e.target.value);
              setValidationResult(null);
            }}
          >
            <Radio value="single">Single Episode</Radio>
            <Radio value="batch">Season Batch</Radio>
          </Radio.Group>
        </Form.Item>

        {mode === 'single' && (
          <>
            <Form.Item label="Search Content">
              <Select
                showSearch
                placeholder="Search episodes by title or slug..."
                filterOption={false}
                onSearch={handleContentSearch}
                onSelect={(value: number) => handleContentSelect(value)}
                loading={contentSearchLoading}
                notFoundContent={contentSearchLoading ? 'Searching...' : 'No results'}
                style={{ width: '100%' }}
              >
                {contentSearchResults.map(content => (
                  <Select.Option key={content.id} value={content.id}>
                    {content.title} ({content.slug})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {selectedContentId && (
              <Form.Item label="Select Subtitles to Burn In">
                {subtitlesLoading ? (
                  <div>Loading subtitles...</div>
                ) : subtitles.length === 0 ? (
                  <Alert
                    type="warning"
                    message="No subtitles found for this content"
                    showIcon
                  />
                ) : (
                  <Space direction="vertical">
                    {subtitles.map(sub => (
                      <Checkbox
                        key={sub.id}
                        checked={selectedSubtitleIds.includes(sub.id)}
                        onChange={e => handleSubtitleToggle(sub.id, e.target.checked)}
                      >
                        {sub.lang.toUpperCase()} (Subtitle ID: {sub.id})
                      </Checkbox>
                    ))}
                  </Space>
                )}
              </Form.Item>
            )}

            <Form.Item>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!canSubmitSingle}
              >
                Create Burn-in Job{selectedSubtitleIds.length > 1 ? 's' : ''}
              </Button>
            </Form.Item>
          </>
        )}

        {mode === 'batch' && (
          <>
            <Form.Item label="Series">
              <Select
                showSearch
                placeholder="Search series..."
                filterOption={false}
                onSearch={handleSeriesSearch}
                onSelect={(value: number) => handleSeriesSelect(value)}
                loading={seriesLoading}
                notFoundContent={seriesLoading ? 'Searching...' : 'No results'}
                style={{ width: '100%' }}
              >
                {seriesOptions.map(series => (
                  <Select.Option key={series.id} value={series.id}>
                    {series.title}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {selectedSeriesId && (
              <Form.Item label="Season">
                <Select
                  placeholder="Select a season"
                  onSelect={(value: number) => handleSeasonSelect(value)}
                  loading={seasonLoading}
                  notFoundContent={seasonLoading ? 'Loading...' : 'No seasons'}
                  style={{ width: '100%' }}
                >
                  {seasonOptions.map(season => (
                    <Select.Option key={season.id} value={season.id}>
                      {season.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item label="Language">
              <Input
                placeholder="e.g. en, ko, es"
                value={batchLang}
                onChange={e => {
                  setBatchLang(e.target.value);
                  setValidationResult(null);
                }}
                style={{ width: 200 }}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  onClick={handleValidate}
                  loading={validating}
                  disabled={!selectedSeasonId || !batchLang}
                >
                  Validate
                </Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={!canSubmitBatch}
                >
                  Create Batch Jobs
                </Button>
              </Space>
            </Form.Item>

            {validationResult && (
              <>
                {validationResult.available.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Alert
                      type="success"
                      message={`${validationResult.available.length} episode(s) available for burn-in`}
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                    <Table<BurnInValidationItem>
                      dataSource={validationResult.available}
                      columns={availableColumns}
                      rowKey="content_id"
                      pagination={false}
                      size="small"
                    />
                  </div>
                )}

                {validationResult.skipped.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Alert
                      type="warning"
                      message={`${validationResult.skipped.length} episode(s) skipped`}
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                    <Table<BurnInSkippedItem>
                      dataSource={validationResult.skipped}
                      columns={skippedColumns}
                      rowKey="content_id"
                      pagination={false}
                      size="small"
                    />
                  </div>
                )}

                {validationResult.available.length === 0 &&
                  validationResult.skipped.length === 0 && (
                    <Alert
                      type="info"
                      message="No episodes found for this season and language combination"
                      showIcon
                    />
                  )}
              </>
            )}
          </>
        )}
      </Form>
    </Component.BasePageComponent>
  );
};

export default observer(BurnInRequest);
